<script setup lang="ts">
import BaseBottomSheetFilter from '@/components/base/BaseBottomSheetFilter.vue'
import BaseFilter from '@/components/base/BaseFilter.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { FilterOption, FilterSection } from '@/types/filter.types'
import { onMounted, reactive, watch } from 'vue'
import ProgramFilter from './ProgramFilter.vue'

const props = defineProps<{
  programs: FilterOption[]
  selectedProgram?: FilterOption
  filters?: {
    level: string
    sort: string
  }
}>()

const emit = defineEmits<{
  (e: 'filter-change', filters: { level: string | number; sort: string | number }): void
  (e: 'program-select', programOption: FilterOption): void
}>()

const isMobile = useIsMobile()

// 필터 옵션 정의
const levelOptions: FilterOption[] = [
  { name: '전체 레벨', idx: 0, data: 'all' },
  { name: '초급', idx: 1, data: 'beginner' },
  { name: '중급', idx: 2, data: 'intermediate' },
  { name: '고급', idx: 3, data: 'advanced' },
]

const sortOptions: FilterOption[] = [
  { name: '최신순', idx: 0, data: 'latest' },
  { name: '오래된순', idx: 1, data: 'oldest' },
]

// 필터 맵 정의
const filterMap = reactive({
  level: {
    title: '레벨',
    options: levelOptions,
    selected: levelOptions[0],
  },
  sort: {
    title: '정렬',
    options: sortOptions,
    selected: sortOptions[0],
  },
})

// props의 필터 값으로 초기화
watch(
  () => props.filters,
  (newFilters) => {
    if (newFilters) {
      const levelOption = levelOptions.find((option) => option.data === newFilters.level)
      const sortOption = sortOptions.find((option) => option.data === newFilters.sort)

      if (levelOption) {
        filterMap.level.selected = levelOption
      }
      if (sortOption) {
        filterMap.sort.selected = sortOption
      }
    }
  },
  { immediate: true },
)

// 바텀시트용 필터 섹션 생성
const getFilterSections = (): FilterSection[] => {
  return Object.entries(filterMap).map(([key, filter]) => ({
    ref: key,
    title: filter.title,
    options: filter.options,
    selectedOption: filter.selected,
  }))
}

// 옵션 선택 핸들러
const handleLevelSelect = (option: FilterOption) => {
  filterMap.level.selected = option
  emit('filter-change', {
    level: option.data,
    sort: filterMap.sort.selected.data,
  })
}

const handleSortSelect = (option: FilterOption) => {
  filterMap.sort.selected = option
  emit('filter-change', {
    level: filterMap.level.selected.data,
    sort: option.data,
  })
}

// 바텀시트에서 저장 시 호출되는 핸들러
const handleSave = (selectedOptions: FilterOption[]) => {
  const sections = getFilterSections()

  // 선택된 옵션 적용
  sections.forEach((section, index) => {
    if (selectedOptions[index] && section.ref in filterMap) {
      const key = section.ref as keyof typeof filterMap
      filterMap[key].selected = selectedOptions[index]
    }
  })

  emit('filter-change', {
    level: filterMap.level.selected.data,
    sort: filterMap.sort.selected.data,
  })
}

const handleProgramSelect = (programOption: FilterOption) => {
  emit('program-select', programOption)
}

onMounted(() => {
  filterMap.level.selected =
    levelOptions.find((option) => option.data === props.filters?.level) ?? levelOptions[0]
  filterMap.sort.selected =
    sortOptions.find((option) => option.data === props.filters?.sort) ?? sortOptions[0]
})
</script>

<template>
  <div class="program-filter-section">
    <ProgramFilter
      :programs="programs"
      :selected-program="props.selectedProgram"
      @program-select="handleProgramSelect"
    />
    <div v-if="!isMobile" class="program-filter-section-right">
      <BaseFilter
        :options="levelOptions"
        :selectedOption="filterMap.level.selected"
        @select="handleLevelSelect"
        menuAlign="end"
      />
      <BaseFilter
        :options="sortOptions"
        :selectedOption="filterMap.sort.selected"
        @select="handleSortSelect"
        menuAlign="end"
      />
    </div>
    <div v-else class="program-filter-section-mobile">
      <BaseBottomSheetFilter :filterSections="getFilterSections()" @save="handleSave" />
    </div>
  </div>
</template>

<style scoped>
.program-filter-section {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.program-filter-section-right {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 16px;
}

@media (max-width: 1365px) {
  .program-filter-section {
    padding: 16px 20px 0px 20px;
  }
}
</style>
